import { useSearchParams } from "react-router-dom";
import { FormRenderer } from "./FormRenderer";
import { useJobRequestForm } from "./hooks/useJobRequestForm";
import { useFormsStore } from "@/dentlab/src/store/Forms";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { SYSTEM_STANDARD_FORM } from "./constants/system-standard-form";

interface VendorJobRequestInterfaceProps {
	vendorId: string;
	vendorName: string;
	myOrgId: string;
	myOrgName: string;
	onSuccess?: () => void;
}

export const VendorJobRequestInterface: React.FC<VendorJobRequestInterfaceProps> = ({
	vendorId,
	vendorName,
	myOrgId,
	myOrgName,
	onSuccess,
}) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const initialFormId = searchParams.get("formId") ?? undefined;

	const forms = useFormsStore((state) => state.formsByOrgId);
	const fetchForms = useFormsStore((state) => state.fetchForms);

	const {
		selection,
		customFormData,
		loading,
		selectedForm,
		setCustomFormData,
		handleFormSelect,
		handleSubmit,
		handleReset,
	} = useJobRequestForm({
		vendors: [vendorId],
		vendorName,
		forms,
		fetchForms,
		myOrgId,
		myOrgName,
		onSuccess,
		initialFormId,
	});

	const vendorForms = forms[vendorId] ? Object.values(forms[vendorId]) : [];
	const formList = vendorForms
		.filter((f) => f.published_at)
		.map((form) => ({ id: form.id, title: form.title }));

	const hasPublishedForms = formList.length > 0;

	const displayFormList = hasPublishedForms
		? formList
		: [{ id: SYSTEM_STANDARD_FORM.id, title: SYSTEM_STANDARD_FORM.title }];

	return (
		<div className="flex h-full gap-6 p-4">
			<div className="w-64 shrink-0">
				<div className="mb-4">
					<h3 className="text-sm font-medium text-gray-500 mb-2">Lieferant</h3>
					<div className="text-base font-semibold">{vendorName}</div>
				</div>
				<div>
					<h3 className="text-sm font-medium text-gray-500 mb-2">Formulare</h3>
					<div className="flex flex-col gap-1">
						{displayFormList.map((form) => {
							const isSelected = selection?.formId === form.id;
							return (
								<button
									key={form.id}
									onClick={() => {
										handleFormSelect(vendorId, form.id);
										setSearchParams({ formId: form.id }, { replace: true });
									}}
									className={cn(
										"px-3 py-2 text-sm text-left rounded-md border transition-colors",
										isSelected
											? "bg-primary text-primary-foreground border-primary"
											: "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
									)}
									aria-label={`Formular ${form.title} auswählen`}
									data-testid={`vendor-form-selector-${form.id}`}
								>
									{form.title}
								</button>
							);
						})}
					</div>
				</div>
			</div>

			<div className="flex-1 overflow-y-auto">
				<FormRenderer
					customForm={selectedForm ?? undefined}
					customFormData={customFormData}
					onCustomFormDataChange={setCustomFormData}
					loading={loading}
					onSubmit={handleSubmit}
					onReset={handleReset}
				/>
			</div>
		</div>
	);
};
